import React, { forwardRef, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Divider, FormLabel, Grid, IconButton, Stack, Typography, useTheme } from '@mui/material';
import FTIcons from 'assets/icons/FTIcons';
import { tokens } from 'theme/tokens';
import { Form, Formik, getIn } from 'formik';
import { deleteDocument, downloadDocument, getPersonnelDocuments, updateDocument, uploadDocument } from 'services/document-service';
import InputField from 'components/Input-Field/input-field';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import ToastHandler from 'components/Toast-Handler/toast-handler';
import SelectInput from '../../components/Select-Field/select-field';
import ReactCardFlip from 'react-card-flip';
import LabelValueBox from 'components/LabelValueBox';
import StatusPill from 'components/StatusPill';

const DQFileManager = forwardRef(({
  showDocumentForm,
  personnelId,
  personnelType,
  documentList,
  onCloseHandler,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const DQ_CATEGORY = {
    'Employment Application': 'DQ_APP_FOR_EMPLOYMENT',
    'License': 'DQ_LICENSE',
    'Pre Employment Screening': 'DQ_PSP_AUTH',
    'Road Test Certificate': 'DQ_CERT_COMPLIANCE_ROAD_TEST',
    'Safety Performance Record': 'DQ_INQUERY_PREV_EMPLOYER_REQUEST',
    'Certificate of Violations': 'DQ_CERT_VIOLATION_AND_MVR_REVEW',
    'Drug & Alcohol Clearing': 'DQ_CLEARINGHOUSE_CONSENT_AUTH',
    'Equipment Lease Agreement': 'DQ_LEASE_AGREEMENT',
    'Medical Certificate': 'DQ_MEDICAL_EXAMINER_VERIFICATION',
    'Drug & Alcohol Record': 'DQ_DRUG_ALCOHOL_POLICY',
    'Vehicle Record': 'DQ_MVR_BG_CHECK_AUTH',
  };
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [files, setFiles] = useState(documentList);

  const [toast, setToast] = useState(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isEditDocument, SetIsEditDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [documentModal, setDocumentModal] = useState({
    file: null,
    fileName: '',
    fileDesc: '',
    referenceId: personnelId,
    fileLabel: '',
    expirationDate: new Date(),
    type: personnelType,
  });
  const fileInput = useRef(null);

  const handleDeleteDocument = () => {
    setIsProcessing(true);
    setToast({ type: 'info', message: 'Deleting Document' });
    deleteDocument(personnelId, selectedDocument.uuid).then((res) => {
      if (res.status === 200) {
        setToast({ type: 'success', message: 'Document deleted successfully.' });
      } else {
        setToast({ type: 'error', message: 'Failed to delete document.' });
      }
    }).catch(() => setToast({ type: 'error', message: 'Failed to delete document.' }))
      .finally(() => {
        loadPersonnelFiles();
      });
  };

  const handleEditDocument = (doc) => {
    const filter = Object.entries(DQ_CATEGORY).filter(e => e[1] === doc.fileLabel)[0];
    setDocumentModal({
      file: doc,
      fileName: doc.fileName,
      fileDesc: doc.fileDesc,
      fileLabel: filter && filter.length === 2 && filter[0],
      expirationDate: new Date(doc.expirationDate).toLocaleDateString(),
      type: doc.role,
    });
    SetIsEditDocument(true);
  };

  const handleDownloadDocument = (doc) => {
    downloadDocument(doc.uuid).then((res) => {
      if (res.status === 200 && res.data?.ok) {
        window.open(res.data.link, '_blank', 'noreferrer');
      } else {
        setToast({ type: 'error', message: 'Failed to download the document.' });
      }
    }).catch(() => setToast({ type: 'error', message: 'Failed to download the document.' }));
  };

  const loadPersonnelFiles = () => {
    getPersonnelDocuments(personnelId).then(res => {
      if (res.status === 200 && res.data) {
        setIsProcessing(false);
        setFiles(res.data);
      } else {
        setToast({ type: 'error', message: 'Failed to get documents.' });
      }
    }).catch(e => {
      setToast({ type: 'error', message: 'Failed to get documents.' + e });
    }).finally(() => {
      setTimeout(() => {
        setToast({});
      }, 10000);
    });
  };

  const closeFileUploadForm = (e) => {
    loadPersonnelFiles();
    SetIsEditDocument(false);
  };

  return (
    <Box
      height={'auto'}
      border={`1px solid ${theme.palette.neutral[200]}`}
      borderRadius={'6px'}
      p={1}
      mt={1}
      backgroundColor={colors.base.white}
    >
      <ReactCardFlip
        isFlipped={!showDocumentForm}
      >
        <Formik
          initialValues={{ documentModal }}
          enableReinitialize={true}
          onSubmit={(values) => {
            const { file, fileLabel, fileDesc, referenceId, type, expirationDate } = values.documentModal;

            const formData = new FormData();
            formData.append('fileLabel', DQ_CATEGORY[fileLabel] || null);
            formData.append('fileDesc', fileDesc || '');
            formData.append('referenceId', referenceId || '');
            formData.append('type', type || '');
            formData.append('expirationDate', new Date(expirationDate).toJSON() || '');

            if (isEditDocument) {
              setIsProcessing(true);
              updateDocument(formData, file.uuid).then(r => {
                if (r.status === 200 && r.data?.ok) {
                  setToast({ type: 'success', message: 'File Uploaded successfully.' });
                  setDocumentModal(...initialValues);
                } else {
                  setToast({ type: 'error', message: 'Failed to upload the file.' });
                }
              }).catch(() => setToast({ type: 'error', message: 'Failed to upload the file.' }))
                .finally(() => {
                  setIsProcessing(false);
                  loadPersonnelFiles();
                });
            } else {
              formData.append('file', file);
              uploadDocument(formData).then(r => {
                if (r.status === 200 && r.data?.ok) {
                  setToast({ type: 'success', message: 'File Uploaded successfully.' });
                  setDocumentModal(...initialValues);
                } else {
                  setToast({ type: 'error', message: 'Failed to upload the file.' });
                }
              }).catch(() => setToast({ type: 'error', message: 'Failed to upload the file.' }))
                .finally(() => {
                  setIsProcessing(false);
                  loadPersonnelFiles();
                });
            }
          }
          }
        >
          {({
            errors,
            handleBlur,
            handleChange,
            touched,
            values,
            setFieldValue,
          }) => {
            const handleFileInput = (e) => {
              const f = e.target.files[0];
              if (f && f.size < 5 * 1024 * 1024) {
                setFieldValue('documentModal.file', f);
                setFieldValue('documentModal.fileName', f.name);
                setIsFileSelected(true);
              } else {
                setFieldValue('documentModal.file', null);
                setFieldValue('documentModal.fileName', '');
                setToast({ type: 'error', message: 'Invalid file or file is too big.' });
              }
            };
            return (
              <Form>
                {isProcessing && <Box textAlign={'center'}><CircularProgress /></Box>}
                {toast && <ToastHandler toast={toast} type={toast.type} />}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <Stack spacing={1} sx={{ alignItems: 'center', mt: 3 }}>
                      <FTIcons.Folder
                        width={40}
                        height={30}
                        fill={colors.main_blue[200]}
                      />
                      <Button
                        variant='outlined'
                        sx={{
                          background: colors.main_blue[50],
                          border: `1px solid ${colors.primary.light}`,
                          color: colors.primary.main
                        }}
                        startIcon={
                          <FTIcons.UploadSimple width={18} height={18} fill={colors.primary.main} />
                        }
                        onClick={() => fileInput.current && fileInput.current.click()}
                      >
                        Select Document
                      </Button>
                      <Typography variant='caption' color={colors.neutral[400]}>Supported format: PDF, Docs, Max file size: 5MB</Typography>
                      <input type='file' onChange={handleFileInput} ref={fileInput} style={{ display: 'none' }} />
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 8 }}>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12 }}>
                        <SelectInput
                          label='Select DQ Category'
                          name='documentModal.fileLabel'
                          value={values.documentModal.fileLabel}
                          variant='outlined'
                          fullWidth
                          error={Boolean(
                            getIn(touched, 'documentModal.fileLabel') &&
                            getIn(errors, 'documentModal.fileLabel')
                          )}
                          helperText={Boolean(
                            getIn(touched, 'documentModal.fileLabel') &&
                            getIn(errors, 'documentModal.fileLabel')
                          )}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          options={Object.keys(DQ_CATEGORY)}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box display={'flex'} gap={2}>
                          <InputField
                            type='text'
                            label='File Name'
                            name='documentModal.fileName'
                            value={values.documentModal.fileName}
                            variant='outlined'
                            fullWidth
                            error={Boolean(
                              getIn(touched, 'documentModal.fileName') &&
                              getIn(errors, 'documentModal.fileName')
                            )}
                            helperText={Boolean(
                              getIn(touched, 'documentModal.fileName') &&
                              getIn(errors, 'documentModal.fileName')
                            )}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            margin='dense'
                            size='small'
                            disabled
                          />
                          {documentModal && documentModal.file && <FormLabel component='legend'>Size: {(documentModal.file.size / 1024).toFixed(2) + ' KB'}</FormLabel>}
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <InputField
                          label='Expiry Date'
                          name='documentModal.expirationDate'
                          value={values.documentModal.expirationDate}
                          type='date'
                          variant='outlined'
                          slotProps={{ inputLabel: { shrink: true } }}
                          fullWidth
                          error={Boolean(
                            getIn(touched, 'documentModal.expirationDate') &&
                            getIn(errors, 'documentModal.expirationDate')
                          )}
                          helperText={Boolean(
                            getIn(touched, 'documentModal.expirationDate') &&
                            getIn(errors, 'documentModal.expirationDate')
                          )}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <InputField
                          type='text'
                          label='File Description'
                          name='documentModal.fileDesc'
                          value={values.documentModal.fileDesc}
                          variant='outlined'
                          fullWidth
                          error={Boolean(
                            getIn(touched, 'documentModal.fileDesc') &&
                            getIn(errors, 'documentModal.fileDesc')
                          )}
                          helperText={Boolean(
                            getIn(touched, 'documentModal.fileDesc') &&
                            getIn(errors, 'documentModal.fileDesc')
                          )}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          margin='dense'
                          size='small'
                          multiline
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Box
                  display='flex'
                  justifyContent='flex-end'
                  p={1}>
                  <Button
                    variant='text'
                    color='secondary'
                    onClick={onCloseHandler}
                  >
                    Close
                  </Button>

                  <Button
                    type='submit'
                    variant='contained'
                    size='small'
                    sx={{
                      background: colors.main_blue[50],
                      border: `1px solid ${colors.primary.light}`,
                      color: colors.primary.main
                    }}
                  >
                    Upload
                  </Button>
                </Box>
              </Form>
            )
          }}
        </Formik>
        <Stack>
          {files.length === 0 ?
            <Typography
              color={colors.neutral[400]}
              variant='body1'
              fontWeight={600}
              textAlign={'center'}
              textTransform={'uppercase'}
            > No Driver Qualification file exists.
            </Typography> :
            files.map((doc, idx) => {
              const isActive = new Date() < new Date(doc.expirationDate);
              return (
                <>
                  <Box key={doc.uuid} container spacing={1} sx={{ display: 'flex', justifyContent: 'space-evenly', wordBreak: 'break-word' }}>
                    <LabelValueBox value={doc.fileLabel} label={'DQ Category'} />
                    <LabelValueBox value={doc.fileName} label={'Document'} />
                    <LabelValueBox value={doc.fileDesc} label={'Description'} />
                    <LabelValueBox value={doc.createdDate ? new Date(doc.createdDate).toDateString().slice(4) : 'None'} label={'Created'} />
                    <LabelValueBox value={doc.lastModifiedDate ? new Date(doc.lastModifiedDate).toDateString().slice(4) : 'None'} label={'Modified'} />
                    <LabelValueBox value={doc.expirationDate ? new Date(doc.expirationDate).toDateString().slice(4) : 'None'} label={'Expiration'} />
                    {isActive ? <StatusPill status={'ACTIVE'} size={'medium'} /> : <StatusPill status={'EXPIRED'} size='medium' />}
                    <Box>
                      <IconButton size='small' onClick={() => { setSelectedDocument(doc); setIsConfirmationDialogOpen(true); }}>
                        <FTIcons.RemoveIcon width={20} height={20} fill={colors.error.main} />
                      </IconButton>
                      <IconButton size='small' onClick={() => handleEditDocument(doc)}>
                        <FTIcons.EditIcon width={20} height={20} fill={colors.info.dark} />
                      </IconButton>
                      <IconButton size='small' onClick={() => handleDownloadDocument(doc)}>
                        <FTIcons.DownloadIcon width={20} height={20} fill={colors.success.dark} />
                      </IconButton>
                    </Box>
                  </Box>
                  {/* Render Divider only if it's not the last item */}
                  {idx < files.length - 1 && <Divider />}
                </>
              )
            })
          }
        </Stack>
      </ReactCardFlip>
      {isConfirmationDialogOpen && <ConfirmationDialog
        open={isConfirmationDialogOpen}
        title={'Delete Document'}
        isProcessing={isProcessing}
        toast={toast}
        handleConfirm={() => handleDeleteDocument()}
        handleCancel={() => setIsConfirmationDialogOpen(false)}
        handleCloseFunc={() => setIsConfirmationDialogOpen(false)}
      >
        {toast && <ToastHandler toast={toast} type={toast.type} />}
        {selectedDocument && <Typography variant='subtitle1'>{`Do you want to delete ${selectedDocument.fileName} ?`}</Typography>}
      </ConfirmationDialog>}
    </Box>
  );
});

export default DQFileManager;